import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Trophy, Play, Flame, Users, Info } from 'lucide-react'
import { MatrixRain } from '@/components/ui/MatrixRain'
import { apiClient } from '@/services/api'
import type { LeaderboardEntry } from '@/types'
import styles from './Leaderboard.module.css'

type Category = 'courses' | 'streaks' | 'students'

function getStatValue(entry: LeaderboardEntry, category: Category): number {
  switch (category) {
    case 'courses': return entry.courses_run
    case 'streaks': return entry.longest_login_streak
    case 'students': return entry.students_hosted
  }
}

function getRankClass(rank: number): string {
  if (rank === 1) return styles.rankGold
  if (rank === 2) return styles.rankSilver
  if (rank === 3) return styles.rankBronze
  return ''
}

const CATEGORY_INFO: Record<Category, { label: string; statLabel: string; description: string }> = {
  courses: {
    label: 'Courses',
    statLabel: 'Courses Run',
    description: 'Ranked by the total number of course runs started. Each time you run a course for students, it counts toward your total.',
  },
  streaks: {
    label: 'Streaks',
    statLabel: 'Longest Streak',
    description: 'Ranked by the longest consecutive days logged in. Log in every day to build your streak!',
  },
  students: {
    label: 'Students',
    statLabel: 'Students Hosted',
    description: 'Ranked by total students enrolled across all your course runs. The more students you teach, the higher you climb.',
  },
}

const CATEGORIES: { key: Category; label: string; icon: React.ReactNode }[] = [
  { key: 'courses', label: 'Courses', icon: <Play className="w-4 h-4" /> },
  { key: 'streaks', label: 'Streaks', icon: <Flame className="w-4 h-4" /> },
  { key: 'students', label: 'Students', icon: <Users className="w-4 h-4" /> },
]

export function Leaderboard() {
  const [category, setCategory] = useState<Category>('courses')
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [currentUserRank, setCurrentUserRank] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    setLoading(true)
    apiClient.getLeaderboard(category)
      .then(res => {
        setEntries(res.data.entries)
        setCurrentUserRank(res.data.current_user_rank)
      })
      .catch(() => {
        setEntries([])
      })
      .finally(() => setLoading(false))
  }, [category])

  return (
    <div className={styles.container}>
      <MatrixRain />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={styles.header}
      >
        <h1 className={styles.title}>
          <Trophy className="inline w-8 h-8 mr-2 align-middle" />
          PIRATE_LEADERBOARD.EXE
        </h1>
        <p className={styles.subtitle}>Top Pirates of The Armada</p>
      </motion.div>

      {/* Category Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={styles.tabsRow}
      >
        <div className={styles.tabs}>
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              className={`${styles.tab} ${category === c.key ? styles.tabActive : ''}`}
              onClick={() => setCategory(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <button
          className={`${styles.infoToggle} ${showInfo ? styles.infoToggleActive : ''}`}
          onClick={() => setShowInfo(v => !v)}
          title="How is this calculated?"
        >
          <Info className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Calculation description */}
      {showInfo && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className={styles.infoBox}
        >
          <p>{CATEGORY_INFO[category].description}</p>
        </motion.div>
      )}

      {/* Your Rank */}
      {!loading && currentUserRank > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={styles.rankBanner}
        >
          <div>
            <p className={styles.rankLabel}>Your Rank</p>
            <p className={styles.rankValue}>#{currentUserRank}</p>
          </div>
          <Trophy className={`w-8 h-8 ${styles.rankTrophy}`} />
        </motion.div>
      )}

      {/* Leaderboard Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className={styles.leaderboardCard}
      >
        <div className={styles.tableHeader}>
          <span>Rank</span>
          <span>Pirate</span>
          <span className={styles.tableHeaderRight}>{CATEGORY_INFO[category].statLabel}</span>
        </div>

        {loading ? (
          <div className={styles.loading}>Loading rankings...</div>
        ) : entries.length === 0 ? (
          <div className={styles.emptyState}>No rankings yet. Start running courses!</div>
        ) : (
          entries.map(entry => (
            <div
              key={entry.user_id}
              className={`${styles.tableRow} ${entry.is_current_user ? styles.currentUserRow : ''}`}
            >
              <span className={`${styles.rank} ${getRankClass(entry.rank)}`}>
                {entry.rank}
              </span>
              <div className={styles.playerInfo}>
                <div className={styles.avatar}>
                  {entry.avatar_url ? (
                    <img src={entry.avatar_url} alt="" className={styles.avatarImg} />
                  ) : (
                    (entry.display_name || '?').charAt(0).toUpperCase()
                  )}
                </div>
                <span className={`${styles.playerName} ${entry.is_current_user ? styles.currentUserName : ''}`}>
                  {entry.display_name}
                  {entry.is_current_user && ' (You)'}
                </span>
              </div>
              <span className={styles.statValue}>
                {getStatValue(entry, category).toLocaleString()}
              </span>
            </div>
          ))
        )}
      </motion.div>
    </div>
  )
}
